import { Link } from "react-router-dom";
import type { Story } from "@/types/content";

interface StoryCardProps {
  story: Story;
  variant?: "default" | "wide" | "compact";
  index?: number;
}

function formatHeat(heat: number) {
  if (heat >= 1000) return `${(heat / 1000).toFixed(1)}k`;
  return String(heat);
}

export default function StoryCard({ story, variant = "default", index }: StoryCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/story/${story.id}`}
        className="group flex gap-4 border-b border-background-200 py-4 last:border-b-0 cursor-pointer"
      >
        {typeof index === "number" && (
          <span className="font-heading text-xl font-bold text-background-400 transition-colors group-hover:text-primary-500">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
        <span className="flex flex-col gap-1.5">
          <span className="font-heading text-base font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
            {story.title}
          </span>
          <span className="flex flex-wrap items-center gap-3 text-xs font-label text-foreground-500">
            <span className="text-primary-600">{story.category}</span>
            <span>{story.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {formatHeat(story.heat)}
            </span>
          </span>
        </span>
      </Link>
    );
  }

  const isWide = variant === "wide";

  return (
    <article
      className={`group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-300 hover:border-primary-300 ${
        isWide ? "md:flex-row" : ""
      }`}
    >
      <Link
        to={`/story/${story.id}`}
        className={`relative block w-full overflow-hidden bg-background-200 ${
          isWide ? "h-56 md:h-auto md:w-2/5 md:shrink-0" : "h-48"
        } cursor-pointer`}
        aria-label={story.title}
      >
        <img
          src={story.cover}
          alt={`${story.title} 配图`}
          title={`${story.title} - ${story.category}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-label font-medium text-background-50">
          {story.category}
        </span>
        {story.verified && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-xs font-label font-medium text-primary-950">
            <i className="ri-verified-badge-line" aria-hidden="true"></i>
            已核实
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-3 text-xs font-label text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-voice-line" aria-hidden="true"></i>
            {story.source}
          </span>
          <span className="h-1 w-1 rounded-full bg-background-400" aria-hidden="true"></span>
          <span>{story.region}</span>
        </div>

        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/story/${story.id}`} className="transition-colors hover:text-primary-700 cursor-pointer">
            {story.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{story.excerpt}</p>

        <div className="mt-auto flex flex-wrap items-center gap-3 pt-4 text-xs font-label text-foreground-500">
          <span>{story.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true"></i>
            {story.readMinutes} 分钟
          </span>
          <span className="flex items-center gap-1 text-primary-600">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {formatHeat(story.heat)}
          </span>
        </div>
      </div>
    </article>
  );
}